import { useMemo, useState, useRef, useEffect, type ReactNode } from 'react'
import {
  flip,
  shift,
  offset,
  useRole,
  useClick,
  Placement,
  useDismiss,
  autoUpdate,
  useFloating,
  useInteractions,
  useListNavigation
} from '@floating-ui/react'
import { ChevronDown } from 'lucide-react'

export interface DropdownOption {
  value: string
  label: string
  description?: string
  icon?: ReactNode
}

export interface DropdownProps {
  options: DropdownOption[]
  value?: string
  disabled?: boolean
  className?: string
  placement?: Placement
  placeholder?: string
  triggerClassName?: string
  searchable?: boolean
  searchPlaceholder?: string
  searchFilter?: (option: DropdownOption, query: string) => boolean
  onChange: (value: string) => void
}

export const Dropdown = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  className = '',
  triggerClassName = '',
  placement = 'bottom-start',
  searchable = false,
  searchPlaceholder = 'Search...',
  searchFilter
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [query, setQuery] = useState('')
  const listRef = useRef<Array<HTMLElement | null>>([])
  const searchRef = useRef<HTMLInputElement | null>(null)

  const { refs, floatingStyles, context, isPositioned } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    strategy: 'absolute',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip({
        fallbackPlacements: ['top-start', 'bottom-start', 'top-end', 'bottom-end']
      }),
      shift({ padding: 8 })
    ]
  })

  const click = useClick(context, { enabled: !disabled })
  const dismiss = useDismiss(context)
  const role = useRole(context)

  // When we render a search input, keep real DOM focus in the input.
  // Floating UI's list navigation uses roving focus, which will steal focus on `activeIndex` updates.
  const enableListNavigation = isOpen && !searchable
  const listNavigation = useListNavigation(context, {
    listRef,
    activeIndex,
    onNavigate: setActiveIndex,
    enabled: enableListNavigation,
    virtual: false
  })

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    searchable ? [click, dismiss, role] : [click, dismiss, role, listNavigation]
  )

  const selectedIndex = options.findIndex((opt) => opt.value === value)
  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : undefined

  const visibleOptions = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!searchable || !q) return options
    const defaultFilter = (opt: DropdownOption) => {
      const haystack = `${opt.label} ${opt.description ?? ''}`.toLowerCase()
      return haystack.includes(q)
    }
    const filter = searchFilter ? (opt: DropdownOption) => searchFilter(opt, q) : defaultFilter
    return options.filter(filter)
  }, [options, query, searchable, searchFilter])

  // Keep keyboard navigation in sync with the selected option when opening
  useEffect(() => {
    if (!isOpen) {
      setActiveIndex(null)
      setQuery('')
      return
    }
    if (searchable) {
      // Focus search after the menu mounts.
      setTimeout(() => searchRef.current?.focus(), 0)
    }
    const selectedVisibleIndex = visibleOptions.findIndex((opt) => opt.value === value)
    setActiveIndex(selectedVisibleIndex >= 0 ? selectedVisibleIndex : 0)
  }, [isOpen, searchable, value, visibleOptions])

  // When the query changes while open, reset active index so arrow keys start at the top of the filtered list.
  useEffect(() => {
    if (!isOpen) return
    setActiveIndex(visibleOptions.length > 0 ? 0 : null)
  }, [query, visibleOptions.length, isOpen])

  const clampIndex = (index: number) => {
    if (visibleOptions.length === 0) return null
    if (index < 0) return visibleOptions.length - 1
    if (index >= visibleOptions.length) return 0
    return index
  }

  const scrollActiveItemIntoView = (index: number | null) => {
    if (index == null) return
    listRef.current[index]?.scrollIntoView({ block: 'nearest' })
  }

  const handleSelect = (optionValue: string) => {
    onChange(optionValue)
    setIsOpen(false)
    setActiveIndex(null)
    setQuery('')
  }

  return (
    <div className={`mp-agent-dropdown ${className}`}>
      <button
        ref={refs.setReference}
        type='button'
        className={`mp-agent-dropdown-trigger ${triggerClassName}`}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup='listbox'
        {...getReferenceProps()}
      >
        <span className='mp-agent-dropdown-trigger-content'>
          {selectedOption ? (
            <>
              {selectedOption.icon && <span className='mp-agent-dropdown-trigger-icon'>{selectedOption.icon}</span>}
              <span className='mp-agent-dropdown-trigger-label'>{selectedOption.label}</span>
            </>
          ) : (
            <span className='mp-agent-dropdown-trigger-placeholder'>{placeholder}</span>
          )}
        </span>
        <ChevronDown size={16} className={`mp-agent-dropdown-chevron ${isOpen ? 'open' : ''}`} />
      </button>
      {isOpen && (
        <div
          role='listbox'
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            opacity: isPositioned ? 1 : 0,
            pointerEvents: isPositioned ? 'auto' : 'none',
            transition: isPositioned ? 'opacity 0.15s ease-out' : 'none'
          }}
          className='mp-agent-dropdown-menu'
          {...getFloatingProps()}
        >
          {searchable && (
            <div className='mp-agent-dropdown-search' onKeyDown={(e) => e.stopPropagation()}>
              <input
                ref={searchRef}
                className='mp-agent-dropdown-search-input'
                value={query}
                placeholder={searchPlaceholder}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  // Escape closes the dropdown
                  if (e.key === 'Escape') {
                    e.preventDefault()
                    setIsOpen(false)
                    setQuery('')
                    return
                  }

                  // Keep focus in the input while still supporting keyboard navigation.
                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    e.preventDefault()
                    const delta = e.key === 'ArrowDown' ? 1 : -1
                    const base = activeIndex == null ? (delta > 0 ? -1 : 0) : activeIndex
                    const next = clampIndex(base + delta)
                    setActiveIndex(next)
                    scrollActiveItemIntoView(next)
                    return
                  }

                  if (e.key === 'Enter') {
                    if (activeIndex == null) return
                    const opt = visibleOptions[activeIndex]
                    if (!opt) return
                    e.preventDefault()
                    handleSelect(opt.value)
                  }
                }}
              />
            </div>
          )}
          <div className='mp-agent-dropdown-list'>
            {visibleOptions.map((option, index) => (
              <button
                key={option.value}
                ref={(node) => {
                  listRef.current[index] = node
                }}
                type='button'
                className={`mp-agent-dropdown-item ${value === option.value ? 'selected' : ''} ${
                  activeIndex === index ? 'active' : ''
                }`}
                role='option'
                aria-selected={value === option.value}
                {...getItemProps({
                  onClick: () => handleSelect(option.value),
                  onMouseEnter: () => {
                    setActiveIndex(index)
                  },
                  onKeyDown: (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      handleSelect(option.value)
                    }
                  }
                })}
              >
                {option.icon && <span className='mp-agent-dropdown-item-icon'>{option.icon}</span>}
                <span className='mp-agent-dropdown-item-content'>
                  <span className='mp-agent-dropdown-item-label'>{option.label}</span>
                  {option.description && <span className='mp-agent-dropdown-item-description'>{option.description}</span>}
                </span>
              </button>
            ))}
            {searchable && visibleOptions.length === 0 && (
              <div className='mp-agent-dropdown-empty' aria-label='No results'>
                No results
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default Dropdown
